import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  FormControl,
  Tooltip,
} from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { getAllEmirates } from "../../../../api/emirates";
import { getRoutesByEmirates } from "../../../../api/routes";
import { getBustopByRouteId } from "../../../../api/bustop";

const ChangeRequest = ({ control, errors }) => {
  const [emirates, setEmirates] = useState([]);
  const [oldRoutes, setOldRoutes] = useState([]);
  const [newRoutes, setNewRoutes] = useState([]);
  const [oldBusStops, setOldBusStops] = useState([]);
  const [newBusStops, setNewBusStops] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BASE_API;

  const fetchEmirates = async () => {
    try {
      setEmirates(await getAllEmirates());
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchRoutesByEmirates = async (emirateId, setRoutes) => {
    try {
      const data = await getRoutesByEmirates(emirateId); // Await the API response
      if (data.status && Array.isArray(data.data)) {
        setRoutes(data.data); // Update the state with the fetched routes
      } else {
        setRoutes([]); // Reset the state if data is not valid
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setRoutes([]); // Optionally reset state on error
    }
  };

  const fetchBustopByRouteId = async (routeId, setBusStops) => {
    try {
      const data = await getBustopByRouteId(routeId); // Await the API response
      if (data.status && Array.isArray(data.data)) {
        setBusStops(data.data); // Update the state with the fetched routes
      } else {
        setBusStops([]); // Reset the state if data is not valid
      }
    } catch (error) {
      console.error("Error fetching bustop:", error);
      setBusStops([]); // Optionally reset state on error
    }
  };

  useEffect(() => {
    fetchEmirates();
  }, []);

  // const fetchRoutes = (emirateId, setRoutes) => {
  //   fetch(`http://localhost:8000/api/routes/by-emirate/${emirateId}`)
  //     .then((response) => response.json())
  //     .then((data) => {
  //       if (data.status && Array.isArray(data.data)) {
  //         setRoutes(data.data);
  //       } else {
  //         setRoutes([]);
  //       }
  //     })
  //     .catch((error) => console.error("Error fetching routes:", error));
  // };

  const fetchBusStops = (routeId, setBusStops) => {
    fetch(`${API_BASE_URL}bustops/route/${routeId}`, {
      headers: {
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBusStops(data.data);
        } else {
          setBusStops([]);
        }
      })
      .catch((error) => console.error("Error fetching bus stops:", error));
  };

  return (
    <Box sx={{ width: "75%", margin: "auto", mt: 3 }}>
      <Paper elevation={0} sx={{ border: "1px solid black", p: 2 }}>
        <Box display="flex" alignItems="center" sx={{ bgcolor: "#e8e8e8", p: 1, mb: 2 }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold" }}>
            Please provide the details if change of residence and bus routes: (BUS OR STOP CHANGE
            REQUEST)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Emirates
            </Typography>
            <Controller
              name="oldEmirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchRoutesByEmirates(event.target.value, setOldRoutes);
                    }}
                    displayEmpty
                    error={!!errors.oldEmirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.oldEmirate && (
                    <Typography color="error" variant="caption">
                      {errors.oldEmirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Emirates
            </Typography>
            <Controller
              name="newEmirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchRoutesByEmirates(event.target.value, setNewRoutes);
                    }}
                    displayEmpty
                    error={!!errors.newEmirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.newEmirate && (
                    <Typography color="error" variant="caption">
                      {errors.newEmirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Bus Route No.
            </Typography>
            <Controller
              name="oldRoute"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchBustopByRouteId(event.target.value, setOldBusStops);
                    }}
                    displayEmpty
                    error={!!errors.oldRoute}
                    inputProps={{ "aria-label": "Select route" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select route</em>
                    </MenuItem>
                    {oldRoutes.map((route) => (
                      <MenuItem
                        key={route.rid}
                        value={route.rid}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{`${route.routename} - ${route.routeno}`}</span>
                        {route.map && (
                          <Tooltip title="Show route on map" arrow>
                            <MapIcon
                              sx={{
                                marginLeft: "auto",
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: "primary.main",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(route.map, "_blank");
                              }}
                            />
                          </Tooltip>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.oldRoute && (
                    <Typography color="error" variant="caption">
                      {errors.oldRoute.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Bus Route No.
            </Typography>
            <Controller
              name="newRoute"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchBustopByRouteId(event.target.value, setNewBusStops);
                    }}
                    displayEmpty
                    error={!!errors.newRoute}
                    inputProps={{ "aria-label": "Select route" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select route</em>
                    </MenuItem>
                    {newRoutes.map((route) => (
                      <MenuItem
                        key={route.rid}
                        value={route.rid}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{`${route.routename} - ${route.routeno}`}</span>
                        {route.map && (
                          <Tooltip title="Show route on map" arrow>
                            <MapIcon
                              sx={{
                                marginLeft: "auto",
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: "primary.main",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(route.map, "_blank");
                              }}
                            />
                          </Tooltip>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.newRoute && (
                    <Typography color="error" variant="caption">
                      {errors.newRoute.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Bus Stop:
            </Typography>
            <Controller
              name="oldBusStop"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.oldBusStop}
                    inputProps={{ "aria-label": "Select pick-up / drop-off point" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select old bus stop
                    </MenuItem>
                    {oldBusStops.map((stop) => (
                      <MenuItem key={stop.sid} value={stop.sid}>
                        {`${stop.pickupcode} - ${stop.bustopname}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.oldBusStop && (
                    <Typography color="error" variant="caption">
                      {errors.oldBusStop.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Bus Stop:
            </Typography>
            <Controller
              name="newBusStop"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.newBusStop}
                    inputProps={{ "aria-label": "Select pick-up / drop-off point" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select new bus stop
                    </MenuItem>
                    {newBusStops.map((stop) => (
                      <MenuItem key={stop.sid} value={stop.sid}>
                        {`${stop.pickupcode} - ${stop.bustopname}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.newBusStop && (
                    <Typography color="error" variant="caption">
                      {errors.newBusStop.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Location:
            </Typography>
            <Controller
              name="oldLocation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.oldLocation}
                  helperText={errors.oldLocation?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Location:
            </Typography>
            <Controller
              name="newLocation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.newLocation}
                  helperText={errors.newLocation?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
          Note:- One month prior notice required for any route change and administrative charge AED
          100/- to be paid (Subject to seat and bus availability)
        </Typography>
      </Paper>
    </Box>
  );
};

ChangeRequest.validationSchema = yup.object().shape({
  oldEmirate: yup.string().required("Old Emirates is required"),
  newEmirate: yup.string().required("New Emirates is required"),
  oldRoute: yup.string().required("Old Bus Route No. is required"),
  newRoute: yup.string().required("New Bus Route No. is required"),
  oldBusStop: yup.string().required("Old Bus Stop is required"),
  newBusStop: yup.string().required("New Bus Stop is required"),
  oldLocation: yup.string().required("Old Location is required"),
  newLocation: yup.string().required("New Location is required"),
});

export default ChangeRequest;
